import React, { PureComponent, Fragment, useState, useEffect } from 'react';
import { InfoCircleOutlined, QuestionCircleOutlined, StopOutlined } from '@ant-design/icons';
import '@ant-design/compatible/assets/index.css';
import {
  Input,
  Button,
  Checkbox,
  Row,
  Col,
  message,
  Modal,
  Tooltip,
  Form,
  Radio,
  Space,
  Divider,
  Switch,
  Steps,
  Popover,
  Select,
} from 'antd';
import { connect } from 'dva';
import Label from '@/components/Label/Label';
import styles from './BasicAWSConfiguration.module.css';
import CoverageStatusConnections from '../CoverageStatusConnections';
import MoodUpdateModal from './MoodUpdateModal';
import { Icon } from '@ant-design/compatible';
import { COLOR } from '@/utils/color';
import ModeCards from '@/components/ModeCards';
import AwsSvgIcon from '@/../public/aws-1.svg';
import TestConnectionContent from './Components/TestConnectionContent';
import DeployStackContent from './Components/DeployStackContent';
import { INSTRUCTION_CONTENT, tooltipContent } from '@/utils/constant';
import ReconnectCloudAccount from '../ReconnectCloudAccount/ReconnectCloudAccount';
import PopoverContent from './Components/PopoverContent';
import { showDeleteConfirm } from '../showDeleteConfirm';
import DeleteCloudAccountWrapper from './Components/DeleteCloudAccountWrapper';
import { decryptAES } from '@/utils/utils';
import { createRoles } from '@/utils/createRoles';
import { isEmpty } from 'lodash';
import ScanRunningInstances from './Components/ScanRunningInstances';

const cardData = [
  {
    title: 'ADVANCED (Recommended)',
    description: `Complete Cloudnosys security suite, including <br/><b>CloudEye:</b> Misconfigurations Detection
    <br/><b>Cloudxray:</b> Malware & OS Vulnerabilities
    <br/><b>EagleEye:</b> Real-time Threat Detection`,
    key: 'advanced',
    status: '',
  },
  {
    title: 'BASIC (CloudEye)',
    description: `Catalog your cloud resources and detect misconfiguration level violations<br/>(Cloud security posture management, CSPM)`,
    key: 'basic',
    status: '',
  },
];

const { confirm, error } = Modal;

const BasicAWSConfiguration = props => {
  const {
    cloudAccountData,
    updateData,
    testConnectionTypes,
    setActiveTab,
    allCloudAccountsList,
  } = props;

  const [reconnectModal, setReconnectModal] = useState(false);
  const [modeCheckbox, setModeCheckbox] = useState(cloudAccountData.protectionLevel);

  const [upgradeMode, setUpgradeMode] = useState(false);
  const [isRolesAlreadyExist, setIsRolesAlreadyExist] = useState(false);

  const [form] = Form.useForm();

  // * Form on finish
  const onFinish = values => {
    updateData(values);
  };

  // * this function is used to validate the form before submit/update
  const onFinishFailed = ({ errorFields }) => {
    form.scrollToField(errorFields[0].name);
  };

  const closeModal = () => {
    setShowDeleteModal(false);
  };

  const showModal = () => {
    setShowDeleteModal(true);
  };

  const handleDisbaledAccount = () => {
    message.success('Cloud Account Disabled Successfully');
    Modal.destroyAll();
  };

  const confirmUpgradeModal = () => {
    confirm({
      title: (
        <p
          style={{
            margin: 0,
            fontSize: '1em',
          }}
        >
          Upgrade Cloud Account?
        </p>
      ),
      content: (
        <p
          style={{
            margin: 0,
            fontSize: '0.8em',
          }}
        >
          Upgrading your cloud account’s protection level to Advanced mode will give you access to
          premium features such as Real-time monitoring, malware detection and CVEs scanning.
        </p>
      ),
      okText: 'Upgrade',
      centered: true,
      async onOk() {
        await checkRolesBeforeUpgrade();
      },
      onCancel() {
        setModeCheckbox(cloudAccountData.protectionLevel);
      },
    });
  };

  const checkRolesBeforeUpgrade = async () => {
    const { dispatch } = props;
    const payload = {
      externalId: cloudAccountData.externalId,
      orgId: cloudAccountData.orgId,
      cloudAccountId: cloudAccountData.id,
      cxrRegion: cloudAccountData.cloudXrayConfig.cxrRegion,
      authorizerSecretKey: cloudAccountData.cloudXrayConfig.authKey,
      roleArn: cloudAccountData.roleArn,
      rolesToCheck: [
        cloudAccountData.adminRoleArn,
        cloudAccountData.execRoleArn,
        cloudAccountData.roleArn,
      ],
      isEditCloudAccount: true,
    };
    try {
      const { data } = await dispatch({
        type: 'cloudAccount/checkRolesExist',
        payload,
      });
      const { allRolesDeployed, allStacksDeployed } = data;

      // if any role is not deployed then show the reconnect modal
      if (!allRolesDeployed) {
        setModeCheckbox(cloudAccountData.protectionLevel);
        setReconnectModal(true);
        Modal.destroyAll();
        return;
      }

      // if all roles are deployed then update the cloud account mode
      if (allRolesDeployed && allStacksDeployed) {
        await handleModeUpdate();
        Modal.destroyAll();
        return;
      }

      // if all roles are deployed but stacks are not deployed then show the deploy stack modal
      if (allRolesDeployed && !allStacksDeployed) {
        setUpgradeMode(true);
        setReconnectModal(false);
        Modal.destroyAll();
        return;
      }
    } catch (err) {
      console.log('Error from checkRolesBeforeUpgrade: ', err);
      setModeCheckbox(cloudAccountData.protectionLevel);
    }
  };

  const getCurrentlyScanningCloudAccountsIds = ({ shouldFilterWithStartScanStatus = true }) => {
    const { allCloudAccountsList } = props;

    // Get currently scanning cloud accounts Ids
    const currentlyScanningCloudAccountsIds = allCloudAccountsList
      .filter(
        cloudAccount =>
          cloudAccount.status === 'running-scan' ||
          (shouldFilterWithStartScanStatus && cloudAccount.status === 'start-scan')
      )
      .map(cloudAccount => cloudAccount.id);

    return currentlyScanningCloudAccountsIds;
  };

  const runScan = accountData => {
    const { dispatch } = props;

    // Get currently scanning cloud accounts Ids
    let currentlyScanningCloudAccountsIds = getCurrentlyScanningCloudAccountsIds({
      shouldFilterWithStartScanStatus: true,
    });

    // Reset scan progress if currently no cloud account is scanning
    if (currentlyScanningCloudAccountsIds.length === 0) {
      dispatch({
        type: 'cloudAccount/clearScanProgress',
      });
    }

    dispatch({
      type: 'scan/runScan',
      payload: {
        cloudAccounts: [accountData],
        orgId: accountData.orgId,
        currentlyScanningCloudAccountsIds,
      },
    });

    dispatch({
      type: 'scopeReport/current',
    });
  };

  const handleModeUpdate = async () => {
    const { dispatch } = props;

    await dispatch({
      type: 'cloudAccounts/updateCloudAccountMode',
      payload: {
        cloudAccountId: cloudAccountData.id,
        protectionLevel: 'advanced',
      },
    });

    const payload = {
      ...cloudAccountData,
      protectionLevel: 'advanced',
    };

    runScan(payload);
  };

  useEffect(() => {
    if (modeCheckbox !== cloudAccountData.protectionLevel) {
      confirmUpgradeModal();
    }
  }, [modeCheckbox]);

  return (
    <>
      <Form onFinish={onFinish} form={form}>
        <Row justify="space-between" gutter={24}>
          <Col span={12}>
            <Row style={{ marginBottom: 44 }}>
              <Col span={24}>
                <Label
                  style={{
                    marginBottom: 10,
                  }}
                  title="Name"
                />

                <Row gutter={18}>
                  <Col span={18}>
                    <Form.Item
                      required
                      name="accountName"
                      initialValue={cloudAccountData.accountName || ''}
                      rules={[
                        {
                          required: true,
                          message: 'Please enter your AWS Account Name',
                        },
                      ]}
                      style={{
                        marginBottom: 0,
                      }}
                    >
                      <Input placeholder="AWS Account Name" />
                    </Form.Item>
                  </Col>
                  <Col
                    span={2}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Popover content={tooltipContent.cloudAccountName}>
                      <QuestionCircleOutlined
                        style={{
                          verticalAlign: 0,
                          fontSize: '20px',
                          color: 'rgba(0, 0, 0, 0.15)',
                        }}
                      />
                    </Popover>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row style={{ marginBottom: 44 }}>
              <Col span={24}>
                <Label
                  style={{
                    marginBottom: 10,
                  }}
                  title="External ID"
                />

                <Row gutter={18}>
                  <Col span={18}>
                    <Form.Item
                      required
                      name="AWSExternalID"
                      initialValue={cloudAccountData.externalId || ''}
                      rules={[
                        {
                          // required: this.state.checkNick,
                          message: 'Please enter your AWS External ID ',
                        },
                      ]}
                      style={{
                        marginBottom: 0,
                      }}
                    >
                      <Input placeholder="AWS External ID" />
                    </Form.Item>
                  </Col>
                  <Col
                    span={2}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Popover
                      content={
                        <PopoverContent fileContent={INSTRUCTION_CONTENT.CHANGE_EXTERNAL_ID} />
                      }
                    >
                      <QuestionCircleOutlined
                        style={{
                          verticalAlign: 0,
                          fontSize: '20px',
                          color: 'rgba(0, 0, 0, 0.15)',
                        }}
                      />
                    </Popover>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row style={{ marginBottom: 44 }}>
              <Col span={24}>
                <Label
                  style={{
                    marginBottom: 10,
                  }}
                  title="Role ARN"
                />
                <Row gutter={18}>
                  <Col span={18}>
                    <Form.Item
                      required
                      name="RoleARN"
                      initialValue={decryptAES(cloudAccountData.roleArn).replaceAll('"', '') || ''}
                      rules={[
                        {
                          message: 'Please enter your Role ARN',
                        },
                      ]}
                      style={{
                        marginBottom: 0,
                      }}
                    >
                      <Input disabled placeholder="Role ARN" />
                    </Form.Item>
                  </Col>
                  <Col
                    span={2}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Popover content={tooltipContent.roleArn}>
                      <QuestionCircleOutlined
                        style={{
                          verticalAlign: 0,
                          fontSize: '20px',
                          color: 'rgba(0, 0, 0, 0.15)',
                        }}
                      />
                    </Popover>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row style={{ marginBottom: 44 }}>
              <Col span={24}>
                <Label
                  style={{
                    marginBottom: 10,
                  }}
                  title="Monitoring"
                />
                <Row gutter={18}>
                  <Col span={18}>
                    <Form.Item
                      required
                      name="interval"
                      initialValue={cloudAccountData.interval || 'Off'}
                      rules={[
                        {
                          message: 'Please select monitoring status',
                        },
                      ]}
                      style={{
                        marginBottom: 0,
                      }}
                    >
                      <Select>
                        <Option value="Off">Off</Option>
                        <Option value="daily">Daily</Option>
                        <Option value="weekly">Weekly</Option>
                        <Option value="monthly">Monthly</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col
                    span={2}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Popover content={tooltipContent.monitoring}>
                      <QuestionCircleOutlined
                        style={{
                          verticalAlign: 0,
                          fontSize: '20px',
                          color: 'rgba(0, 0, 0, 0.15)',
                        }}
                      />
                    </Popover>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Col>
                <Label title="Account Status" />
              </Col>
              <Col>
                <Form.Item
                  required
                  name="isActive"
                  initialValue={cloudAccountData.isActive}
                  style={{
                    marginBottom: '1.2rem',
                    marginLeft: '1rem',
                  }}
                >
                  <Switch
                    size="small"
                    checkedChildren={<Icon type="check" />}
                    unCheckedChildren={<Icon type="close" />}
                    defaultChecked={cloudAccountData.isActive}
                  />
                </Form.Item>
              </Col>
            </Row>
            <p>
              <p>
                Currently your Account is &nbsp;
                <b
                  style={{
                    color: cloudAccountData.isActive ? COLOR.ACTIVE : COLOR.DISABLED,
                  }}
                >
                  {cloudAccountData.isActive ? 'Active' : 'Inactive'}
                </b>
              </p>
            </p>

            <Form.Item style={{ margin: 0, marginTop: 44 }}>
              <Button
                loading={props.updating}
                type="primary"
                htmlType="submit"
                style={{ float: 'left' }}
              >
                Update
              </Button>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Label
              style={{
                marginBottom: 10,
              }}
              title="Protection Level"
            />
            {cardData.map((data, index) => (
              <ModeCards
                isLastIndex={index === cardData.length - 1}
                data={data}
                modeCheckbox={modeCheckbox || 'basic'}
                setModeCheckbox={setModeCheckbox}
                provider={cloudAccountData.provider}
              />
            ))}
          </Col>
        </Row>
      </Form>

      <Divider />

      <DeleteCloudAccountWrapper
        handleDeleteAccount={props.handleDeleteAccount}
        handleDisbaledAccount={props.handleDisbaledAccount}
        showDisableAction={cloudAccountData.isActive}
        cloudAccountName={cloudAccountData.accountName}
        cloudAccountData={cloudAccountData}
      />

      <UpgradeModeModal
        open={upgradeMode}
        setOpen={setUpgradeMode}
        props={props}
        testConnectionTypes={testConnectionTypes}
        setModeCheckbox={setModeCheckbox}
        allCloudAccountsList={allCloudAccountsList}
      />

      <ReconnectModal
        open={reconnectModal}
        setOpen={setReconnectModal}
        setModeCheckbox={setModeCheckbox}
        cloudAccountData={cloudAccountData}
      />
    </>
  );
};

export default connect(({ cloudAccount }) => ({
  updating: cloudAccount.updating,
  scanRunningInstances: cloudAccount.scanRunningInstances,
  deployStackButtonLoading: cloudAccount.deployStackButtonLoading,
  deployStackCheckboxDisabled: cloudAccount.deployStackCheckboxDisabled,
  testConnectionTypes: cloudAccount.testConnectionTypes,
  allCloudAccountsList: cloudAccount.allCloudAccountsList,
}))(BasicAWSConfiguration);

const UpgradeModeModal = ({ open, setOpen, props, testConnectionTypes, setModeCheckbox }) => {
  const [current, setCurrent] = useState(0);
  const [deployedCftCheck, setDeployedCftCheck] = useState(false);
  const [cxrRegion, setCXRRegion] = useState();

  const {
    cloudAccountData,
    dispatch,
    deployStackButtonLoading,
    deployStackCheckboxDisabled,
  } = props;

  useEffect(() => {
    // * Unmount
    return () => {
      onUnmount();
    };
  }, []);

  const handleFinishAndRunScan = () => {
    // update the cloud account data and then run scan on it
    // 1. update the cloud account data for mode/ protection level updated to advanced
    const { dispatch } = props;
    dispatch({
      type: 'cloudAccounts/updateCloudAccountMode',
      payload: {
        cloudAccountId: cloudAccountData.id,
        protectionLevel: 'advanced',
      },
    });
    const payload = {
      ...cloudAccountData,
      protectionLevel: 'advanced',
    };

    runScan(payload);
  };

  const runScan = accountData => {
    const { dispatch } = props;

    // Get currently scanning cloud accounts Ids
    let currentlyScanningCloudAccountsIds = getCurrentlyScanningCloudAccountsIds({
      shouldFilterWithStartScanStatus: true,
    });

    // Reset scan progress if currently no cloud account is scanning
    if (currentlyScanningCloudAccountsIds.length === 0) {
      dispatch({
        type: 'cloudAccount/clearScanProgress',
      });
    }

    dispatch({
      type: 'scan/runScan',
      payload: {
        cloudAccounts: [accountData],
        orgId: accountData.orgId,
        currentlyScanningCloudAccountsIds,
      },
    });

    dispatch({
      type: 'scopeReport/current',
    });
  };

  const getCurrentlyScanningCloudAccountsIds = ({ shouldFilterWithStartScanStatus = true }) => {
    const { allCloudAccountsList } = props;

    // Get currently scanning cloud accounts Ids
    const currentlyScanningCloudAccountsIds = allCloudAccountsList
      .filter(
        cloudAccount =>
          cloudAccount.status === 'running-scan' ||
          (shouldFilterWithStartScanStatus && cloudAccount.status === 'start-scan')
      )
      .map(cloudAccount => cloudAccount.id);

    return currentlyScanningCloudAccountsIds;
  };

  const handleOk = () => {
    console.log('ok');
  };

  const handleCancel = () => {
    setModeCheckbox(cloudAccountData.protectionLevel);
    setOpen(false);
    onUnmount();
  };

  const updateAccountAndTestConnection = async () => {
    const { dispatch, scanRunningInstances } = props;

    const payloadData = {
      cloudXrayConfig: {
        ...cloudAccountData.cloudXrayConfig,
        scanRunningInstances: scanRunningInstances,
        updatedAt: new Date(),
      },
    };

    await dispatch({
      type: 'cloudAccount/update',
      payload: {
        params: {
          type: 'edit',
          provider: cloudAccountData.provider,
          orgId: cloudAccountData.orgId,
          id: cloudAccountData.id,
        },
        data: payloadData,
      },
    });
  };

  const next = () => {
    setCurrent(current + 1);
    updateAccountAndTestConnection();
  };
  const prev = () => {
    setCurrent(current - 1);
  };

  const handleDeployStack = cloudXrayRegion => {
    setCXRRegion(cloudXrayRegion);
    dispatch({
      type: 'cloudAccount/generateUrl',
      payload: {
        type: 'deployStack',
        cloudAccountId: cloudAccountData.id,
        region: cloudXrayRegion,
        roleArn: cloudAccountData.roleArn, // this should be decrypt first
        organisationId: cloudAccountData.orgId,
        externalId: cloudAccountData.externalId,
        cloudXrayConfig: cloudAccountData.cloudXrayConfig,
        isEditCloudAccount: true,
        provider: 'aws',
      },
    });
  };

  const isAllConnectionConnected = () =>
    !isEmpty(testConnectionTypes) &&
    Object.keys(testConnectionTypes).every(key => testConnectionTypes[key].status === 200);

  const disabledFinishBtnTooltip = () => {
    if (isEmpty(testConnectionTypes)) {
      return '';
    }
    // if any test is in loading then send loading tooltip
    const isConnectionsLoading = Object.keys(testConnectionTypes).some(
      key => testConnectionTypes[key].loading
    );
    if (isConnectionsLoading) return 'Please wait while we test your connection';

    // if any test is failed then send failed tooltip
    const isConnectionsFailed = Object.keys(testConnectionTypes).some(
      key => testConnectionTypes[key].status !== 200
    );
    if (isConnectionsFailed) return 'Please fix the failed connection';

    // if all test is success then send success tooltip with text finish reconnect and scan
    const isConnectionsSuccess = Object.keys(testConnectionTypes).every(
      key => testConnectionTypes[key].status === 200
    );
    if (isConnectionsSuccess) return 'Finish Reconnect & Scan';

    return '';
  };

  const onUnmount = () => {
    setDeployedCftCheck(false);
    setCurrent(0);
    const { dispatch } = props;
    dispatch({
      type: 'cloudAccount/save',
      payload: {
        deployStackButtonLoading: false,
        deployStackCheckboxDisabled: true,
      },
    });
  };

  // steps for modal content
  const steps = [
    {
      title: 'Deploy Stack',
      content: (
        <>
          <DeployStackContent
            deployStackButtonLoading={deployStackButtonLoading}
            deployStackCheckboxDisabled={deployStackCheckboxDisabled}
            handleDeployStack={handleDeployStack}
            deployedCftCheck={deployedCftCheck}
            setDeployedCftCheck={setDeployedCftCheck}
          />
          <ScanRunningInstances deployedCftCheck={deployedCftCheck} />
        </>
      ),
    },
    {
      title: 'Testing',
      content: (
        <TestConnectionContent
          isEditCloudAccount={true}
          cloudAccountData={cloudAccountData}
          byPassAndTestAll={true}
          connectionProtectionType="advanced"
          cxrRegion={cxrRegion}
        />
      ),
    },
  ];

  const items = steps.map(item => ({
    key: item.title,
    title: item.title,
  }));

  const buttonsActions = [
    {
      firstBtn: 'Cancel',
      secondBtn: 'Test Connection',
      onClickFirstBtn: handleCancel,
      onClickSecondBtn: next,
      disabledSecondBtn:
        deployStackButtonLoading || deployStackCheckboxDisabled || !deployedCftCheck,
      disabledSecondBtnTooltipText: deployStackCheckboxDisabled
        ? 'Please deploy CFT first'
        : 'Acknowledge you have deployed required stack',
    },
    {
      firstBtn: 'Previous',
      secondBtn: 'Finish and Run Scan',
      onClickFirstBtn: prev,
      onClickSecondBtn: handleFinishAndRunScan,
      disabledSecondBtn: !isAllConnectionConnected(),
      disabledSecondBtnTooltipText: disabledFinishBtnTooltip(),
    },
  ];
  return (
    <Modal
      title="Upgrade Protection Level"
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      width={1000}
      destroyOnClose
      bodyStyle={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}
      footer={[
        <Button key="back" onClick={buttonsActions[current].onClickFirstBtn}>
          {buttonsActions[current].firstBtn}
        </Button>,
        <Tooltip
          style={{
            marginLeft: '14px',
          }}
          placement="bottom"
          title={buttonsActions[current].disabledSecondBtnTooltipText}
        >
          <Button
            key="submit"
            type="primary"
            onClick={buttonsActions[current].onClickSecondBtn}
            disabled={buttonsActions[current].disabledSecondBtn}
            style={{
              marginLeft: '14px',
            }}
          >
            {buttonsActions[current].secondBtn}
          </Button>
        </Tooltip>,
      ]}
    >
      <>
        <Steps size="small" current={current} items={items} />
        {steps[current].content}
      </>
    </Modal>
  );
};

const ReconnectModal = ({ open, setOpen, setModeCheckbox, cloudAccountData }) => {
  return (
    <Modal
      width={1000}
      footer={false}
      open={open}
      onCancel={() => {
        setModeCheckbox(cloudAccountData.protectionLevel);
        setOpen(false);
      }}
      destroyOnClose
      bodyStyle={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          Reconnect&nbsp;
          <span
            style={{
              color: '#1890ff',
              fontWeight: 'bold',
            }}
          >
            {cloudAccountData.accountName}
          </span>
          &nbsp;to Cloudnosys
        </div>
      }
    >
      <ReconnectCloudAccount
        onClose={() => {
          setModeCheckbox('advanced');
          setOpen(false);
        }}
        cloudAccountData={cloudAccountData}
        updateProtecionLevelTo="advanced"
        isRolesMissing={true}
        isForceFullyReconnecting={true}
      />
    </Modal>
  );
};
